import { useState, useRef, useEffect } from 'preact/hooks';
import { ChatCircle, X, PaperPlaneRight, Robot, User, SpinnerGap } from '@phosphor-icons/react';
import { getSessionToken } from '../auth/LoginScreen.js';

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

export function AiChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', text: 'Привет! Я AI-ассистент CryptoDash. Спроси меня о фермах, прибыльности или статистике.' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = '';
      inputRef.current?.focus();
    }
  }, [open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);

    const apiMessages = [...messages, { role: 'user' as const, text }].map((m, i) => ({
      id: `msg-${i}`,
      role: m.role,
      parts: [{ type: 'text', text: m.text }],
    }));

    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = getSessionToken();
      if (token) headers['x-session-token'] = token;

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({ messages: apiMessages }),
      });

      if (!res.ok) throw new Error('API error');

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let assistantText = '';

      setMessages((prev) => [...prev, { role: 'assistant', text: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'text-delta' && parsed.delta) {
              assistantText += parsed.delta;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', text: assistantText };
                return updated;
              });
            }
          } catch {
            // skip non-JSON lines
          }
        }
      }

      if (!assistantText) {
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', text: 'Не удалось получить ответ.' };
          return updated;
        });
      }
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: 'Ошибка соединения с AI.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button
        class="ai-chat-fab"
        onClick={() => setOpen(true)}
        title="AI Ассистент"
      >
        <ChatCircle size={22} weight="duotone" />
      </button>
    );
  }

  return (
    <div class="ai-chat-panel">
      <div class="ai-chat-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span style={{ color: 'var(--cyan)', display: 'flex' }}><Robot size={16} /></span>
          <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>CryptoDash AI</span>
        </div>
        <button class="btn-icon" onClick={() => setOpen(false)} style={{ width: '26px', height: '26px' }}>
          <X size={14} />
        </button>
      </div>

      <div class="ai-chat-messages">
        {messages.map((msg, i) => (
          <div key={i} class={`ai-msg ai-msg-${msg.role}`}>
            <div class="ai-msg-icon">
              {msg.role === 'assistant' ? <Robot size={14} /> : <User size={14} />}
            </div>
            <div class="ai-msg-text">{msg.text}</div>
          </div>
        ))}
        {loading && messages[messages.length - 1]?.text === '' && (
          <div class="ai-msg ai-msg-assistant">
            <div class="ai-msg-icon"><Robot size={14} /></div>
            <div class="ai-msg-text"><span style={{ display: 'flex', animation: 'spin 1s linear infinite' }}><SpinnerGap size={14} /></span></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div class="ai-chat-input">
        <input
          ref={inputRef}
          type="text"
          placeholder="Спросите что-нибудь..."
          value={input}
          onInput={(e) => setInput((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          disabled={loading}
        />
        <button onClick={sendMessage} disabled={loading || !input.trim()}>
          <PaperPlaneRight size={16} />
        </button>
      </div>
    </div>
  );
}
